import { useCallback, useEffect, useState } from 'react';
import {
  Navigate,
  Route as RouterRoute,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AppShell, type Route } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { OrganizationDetailPage } from './pages/organization';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { AkquisePage } from './pages/akquise';
import { CampaignDetailPage } from './pages/leads/CampaignDetailPage';
import { RepliesPage } from './pages/leads/RepliesPage';
import { DashboardPage } from './pages/DashboardPage';
import { InboxPage } from './pages/InboxPage';
import { TasksPage } from './pages/TasksPage';
import { LoginPage } from './pages/LoginPage';
import { SettingsPage } from './pages/SettingsPage';
import {
  fetchCurrentUser,
  logout,
  setUnauthenticatedHandler,
  type CurrentUser,
} from './lib/crm-client';

/**
 * Die Seiten der Anwendung.
 *
 * Die deutschen Pfade sind Absicht: die Oberfläche ist deutsch, und die URL ist
 * Teil der Oberfläche. `Route` ist der Schlüssel der sechs Navigationspunkte
 * (plus Einstellungen); mehrere Pfade dürfen auf denselben Punkt zeigen, damit
 * die Seitenleiste dort markiert bleibt, wo eine Seite „darunter“ liegt.
 *
 * `/akquise` ist eine Seite mit fünf Reitern (`?reiter=`, siehe `pages/akquise`).
 * Die alten Pfade `/leads/*`, `/verloren` und `/import` leiten auf den
 * passenden Reiter um (`REDIRECTS`). Geblieben sind die Akten darunter
 * (`/leads/lead/:id`, `/leads/kampagnen/:id`) und `/leads/antworten`, das in
 * keinen der fünf Reiter passt. Die Antworten stehen als Abschnitt auf *Heute*;
 * dessen Link „Alle Antworten“ führt hierher, denn die Seite behält, was
 * *Heute* nicht anbietet (Deal anlegen, als Kontakt übernehmen, Sequenz ohne
 * Bewertung beenden).
 * Kunden sind Firmen: `/kunden` gibt es als Seite nicht mehr und leitet auf den
 * Statusfilter „Kunden“ der Firmenliste um. Befördern, Zurückstufen und das
 * Anlegen eines Kunden haben ihr Zuhause in der Firmenakte und in der Liste.
 */
const ROUTES: { path: string; key: Route; title: string; element: JSX.Element }[] = [
  { path: '/', key: 'heute', title: 'Heute', element: <DashboardPage /> },
  { path: '/posteingang', key: 'posteingang', title: 'Posteingang', element: <InboxPage /> },
  { path: '/akquise', key: 'akquise', title: 'Akquise', element: <AkquisePage /> },
  { path: '/leads/antworten', key: 'akquise', title: 'Antworten', element: <RepliesPage /> },
  { path: '/projekte', key: 'projekte', title: 'Projekte', element: <ProjectsPage /> },
  { path: '/firmen', key: 'firmen', title: 'Firmen', element: <OrganizationsPage /> },
  { path: '/aufgaben', key: 'aufgaben', title: 'Aufgaben', element: <TasksPage /> },
  { path: '/einstellungen', key: 'einstellungen', title: 'Einstellungen', element: <SettingsPage /> },
];

/** Wohin ein Klick auf einen Navigationspunkt führt. */
const PATH_BY_KEY: Record<Route, string> = {
  heute: '/',
  posteingang: '/posteingang',
  akquise: '/akquise',
  projekte: '/projekte',
  firmen: '/firmen',
  aufgaben: '/aufgaben',
  einstellungen: '/einstellungen',
};

/**
 * Alte Adressen, die weiterleiten, damit Lesezeichen und alte Links nicht ins
 * Leere führen. Die Query der alten Adresse wird mitgenommen (`/auftraege?q=…`
 * aus der Suche, `/leads?liste=…`); steht derselbe Schlüssel im Ziel, gewinnt
 * das Ziel. `umbenennen` hebt Schlüssel um, die im alten Pfad etwas anderes
 * hießen: `/leads/freigaben?reiter=beendet` meinte die Ansicht innerhalb der
 * Freigaben, `reiter` wählt heute den Reiter der Akquise.
 */
const REDIRECTS: { from: string; to: string; umbenennen?: Record<string, string> }[] = [
  { from: '/kontakte', to: '/firmen' },
  { from: '/kunden', to: '/firmen?status=kunde' },
  { from: '/pipelines', to: '/akquise' },
  { from: '/auftraege', to: '/projekte' },
  { from: '/dateien', to: '/projekte' },
  { from: '/automations', to: '/einstellungen' },
  { from: '/kalender', to: '/projekte' },
  { from: '/protokoll', to: '/' },
  // Die Akquise-Reiter. `/leads/kampagnen/:id` und `/leads/lead/:id` sind
  // eigene Seiten und bleiben.
  { from: '/leads', to: '/akquise?reiter=leads' },
  { from: '/leads/listen', to: '/akquise?reiter=listen' },
  { from: '/leads/kampagnen', to: '/akquise?reiter=kampagnen' },
  {
    from: '/leads/freigaben',
    to: '/akquise?reiter=freigaben',
    umbenennen: { reiter: 'ansicht' },
  },
  { from: '/verloren', to: '/akquise?filter=verloren' },
  { from: '/import', to: '/akquise?reiter=listen&import=1' },
];

function Umleitung({ to, umbenennen }: { to: string; umbenennen?: Record<string, string> }) {
  const { search } = useLocation();
  const [pfad = '', zielQuery = ''] = to.split('?');
  const query = new URLSearchParams();
  for (const [schluessel, wert] of new URLSearchParams(search)) {
    query.set(umbenennen?.[schluessel] ?? schluessel, wert);
  }
  for (const [schluessel, wert] of new URLSearchParams(zielQuery)) {
    query.set(schluessel, wert);
  }
  const rest = query.toString();
  return <Navigate to={rest ? `${pfad}?${rest}` : pfad} replace />;
}

export function App() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [checking, setChecking] = useState(true);

  // Läuft die Sitzung während der Nutzung ab, meldet der Client das hier —
  // die Oberfläche fällt dann auf die Anmeldemaske zurück, statt bei jedem
  // Klick eine Fehlermeldung zu zeigen.
  useEffect(() => {
    setUnauthenticatedHandler(() => setUser(null));
  }, []);

  useEffect(() => {
    let abgebrochen = false;
    void fetchCurrentUser().then((u) => {
      if (abgebrochen) return;
      setUser(u);
      setChecking(false);
    });
    return () => {
      abgebrochen = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Lade …
      </div>
    );
  }

  if (!user) return <LoginPage onSignedIn={setUser} />;

  return <SignedInApp user={user} onSignedOut={() => setUser(null)} />;
}

function SignedInApp({
  user,
  onSignedOut,
}: {
  user: CurrentUser;
  onSignedOut: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Die Detailseiten haben keinen eigenen Navigationspunkt — sie markieren den
  // Bereich, aus dem sie kommen. Dieselbe Kontaktakte liegt deshalb unter zwei
  // Adressen: `/kontakte/:id` (Firmen) und `/leads/lead/:id` (Akquise).
  const active =
    ROUTES.find((r) => r.path === location.pathname) ??
    (location.pathname.startsWith('/kontakte/')
      ? ROUTES.find((r) => r.key === 'firmen')
      : undefined) ??
    (location.pathname.startsWith('/firmen/')
      ? ROUTES.find((r) => r.key === 'firmen')
      : undefined) ??
    (location.pathname.startsWith('/leads/')
      ? ROUTES.find((r) => r.key === 'akquise')
      : undefined) ??
    (location.pathname.startsWith('/projekte/')
      ? ROUTES.find((r) => r.key === 'projekte')
      : undefined) ??
    (ROUTES[0] as (typeof ROUTES)[number]);

  const onNavigate = useCallback(
    (key: Route) => navigate(PATH_BY_KEY[key]),
    [navigate],
  );

  const onLogout = useCallback(async () => {
    await logout();
    onSignedOut();
  }, [onSignedOut]);

  return (
    <AppShell
      route={active.key}
      onNavigate={onNavigate}
      userLabel={user.name ?? user.email}
      onLogout={() => void onLogout()}
      topbar={<span className="text-sm font-semibold app-no-drag">{active.title}</span>}
    >
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          {ROUTES.map((r) => (
            <RouterRoute key={r.path} path={r.path} element={r.element} />
          ))}
          <RouterRoute path="/kontakte/:contactId" element={<ContactDetailPage />} />
          <RouterRoute path="/leads/lead/:contactId" element={<ContactDetailPage />} />
          {REDIRECTS.map((r) => (
            <RouterRoute key={r.from} path={r.from} element={
                <Umleitung to={r.to} {...(r.umbenennen ? { umbenennen: r.umbenennen } : {})} />
              }
            />
          ))}
          <RouterRoute
            path="/firmen/:organizationId"
            element={<OrganizationDetailPage />}
          />
          <RouterRoute path="/projekte/:projectId" element={<ProjectDetailPage />} />
          <RouterRoute
            path="/leads/kampagnen/:campaignId"
            element={<CampaignDetailPage />}
          />
          <RouterRoute path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </AppShell>
  );
}
